// /invitations/accept?token=… (FR-41, FR-42): the link in a staff invitation
// email. Signed out, the token waits in sessionStorage while the person signs
// in, then they come back here; signed in, the invitation is accepted at once.
import { Alert, Anchor, Button, Loader, Stack, Text } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../api/client";
import { errorMessage } from "../../shared/session/errors";
import {
  ACCEPT_INVITATION_PATH,
  clearInvitation,
  pendingInvitation,
  saveInvitation,
} from "../../shared/session/invitation";
import { useSession } from "../../shared/session/session";
import { FormError, Page } from "../../shared/ui/Page";
import { MINE_KEY, type MyCompany } from "./common";

export function AcceptInvitePage() {
  const { t } = useTranslation();
  const session = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const linkToken = params.get("token");
  const [joined, setJoined] = useState<MyCompany | null>(null);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  // Keep the token out of the address bar and history once it is stored.
  useEffect(() => {
    if (!linkToken) return;
    saveInvitation(linkToken);
    navigate(ACCEPT_INVITATION_PATH, { replace: true });
  }, [linkToken, navigate]);

  const token = linkToken ?? pendingInvitation();

  useEffect(() => {
    if (session.status !== "signed-in" || linkToken || !token || started.current) return;
    started.current = true;
    void (async () => {
      const { data, error } = await api.POST("/auth/accept-invite", { body: { token } });
      clearInvitation(); // single use: a failed token is not worth retrying either
      if (!data) return setError(errorMessage(error, t("error.generic")));
      setJoined(data);
      void queryClient.invalidateQueries({ queryKey: MINE_KEY });
    })();
  }, [session.status, linkToken, token, queryClient, t]);

  if (session.status === "loading" || linkToken) return null;
  if (!joined && !error && !token) {
    return (
      <Page title={t("invitation.title")}>
        <Alert color="red" role="alert" data-testid="invitation-missing">
          {t("invitation.missing")}
        </Alert>
      </Page>
    );
  }
  if (session.status === "signed-out") {
    return <Navigate to={`/signin?next=${encodeURIComponent(ACCEPT_INVITATION_PATH)}`} replace />;
  }

  return (
    <Page title={t("invitation.title")}>
      {joined ? (
        <Stack gap="sm" data-testid="invitation-accepted">
          <Text>
            {t("invitation.joined", {
              name: joined.company.name,
              role: t(`staff_role.${joined.role}`),
            })}
          </Text>
          <Button component={Link} to={`/business/${joined.company.id}`} w="fit-content">
            {t("invitation.open", { name: joined.company.name })}
          </Button>
        </Stack>
      ) : error ? (
        <Stack gap="sm">
          <FormError message={error} />
          <Anchor component={Link} to="/business">
            {t("invitation.to_business")}
          </Anchor>
        </Stack>
      ) : (
        <Loader size="sm" aria-label={t("invitation.accepting")} />
      )}
    </Page>
  );
}
